import { CommonModule } from '@angular/common';
import { Component, signal, inject, output, OnInit } from '@angular/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Category } from '@vendasta/platform-integrations';
import { CardDataService } from '../card-data.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Component({
  selector: 'platform-integration-category',
  standalone: true,
  imports: [
    CommonModule,
    GalaxyLoadingSpinnerModule,
    GalaxyCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
  ],
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss'],
})
export default class CategoryComponent implements OnInit {
  protected isLoading = false;
  protected isExpanded = true;
  readonly error = signal<string | null>(null);
  readonly categoryControls = signal(new Map<string, FormControl<boolean | null>>());
  readonly selectedCategories = signal(new Set<string>());

  categorySelectionChange = output<string[]>();

  private readonly snackbarService = inject(SnackbarService);
  private readonly cardDataService = inject(CardDataService);

  private handleError = () => {
    this.error.set('Failed to load categories');
    this.isLoading = false;
    return of([]);
  };

  readonly categories = toSignal(
    this.cardDataService.getCategoriesList().pipe(
      tap(() => {
        this.isLoading = true;
        this.error.set(null);
      }),
      map((categories) => {
        this.updateCategoryControls(categories);
        this.isLoading = false;
        return categories;
      }),
      catchError(this.handleError),
    ),
    { initialValue: [] },
  );

  ngOnInit(): void {
    this.categories();
  }

  private updateCategoryControls(categories: Category[]): void {
    const currentControls = this.categoryControls();
    const newControls = new Map(currentControls);

    categories.forEach((category) => {
      if (!newControls.has(category.categoryKey)) {
        const control = new FormControl<boolean | null>(this.selectedCategories().has(category.categoryKey));
        control.valueChanges.subscribe((checked) => {
          if (checked !== null) {
            this.onCategoryChange(category.categoryKey, checked);
          }
        });
        newControls.set(category.categoryKey, control);
      }
    });

    this.categoryControls.set(newControls);
  }

  getCategoryControl(categoryKey: string): FormControl<boolean | null> {
    return this.categoryControls().get(categoryKey) ?? new FormControl<boolean | null>(false);
  }

  toggleCategories(): void {
    this.isExpanded = !this.isExpanded;
  }

  private onCategoryChange(categoryKey: string, checked: boolean): void {
    const current = new Set(this.selectedCategories());
    checked ? current.add(categoryKey) : current.delete(categoryKey);
    this.selectedCategories.set(current);
    this.categorySelectionChange.emit(Array.from(current));
  }
}
